<template>
  <footer class="container-fluid px-0 py-3">
    <div ref="footerRef" class="footer container">
  
      <!-- Main footer body -->
      <div class="footer-body">
        <div class="footer-main">
          <!-- Contact column -->
          <div class="footer-contact">
            <h2 class="contact-title text-white">CONTACTO</h2>
            <div class="contact-info mt-3">
              <div class="contact-item d-flex gap-3">
                <i class="fas fa-envelope mt-1"></i>
                <a class="text-white text-decoration-none" :href="contactMailto">{{ contactEmail }}</a>
              </div>
              <div class="contact-item d-flex gap-3">
                <i class="fas fa-phone mt-1"></i>
                <a class="text-white text-decoration-none" :href="telHref">{{ contactPhoneDisplay }}</a>
              </div>
              <div v-if="instagramUrl" class="contact-item d-flex gap-3">
                <i class="fab fa-instagram mt-1"></i>
                <a class="text-white text-decoration-none" :href="instagramUrl" target="_blank" rel="noopener noreferrer">Instagram</a>
              </div>
            </div>
          </div>
  
          <!-- Link columns -->
          <div class="footer-links-grid">
            <div class="footer-col">
              <h3 class="footer-col-title">Productos</h3>
              <ul class="footer-col-links">
                <li><a href="/productos">Ver todos</a></li>
                <li v-for="cat in footerCategories" :key="cat.id">
                  <a :href="'/productos?category_name=' + encodeURIComponent(cat.name)">{{ cat.name }}</a>
                </li>
              </ul>
            </div>
            
            <div class="footer-col">
              <h3 class="footer-col-title">Ayuda</h3>
              <ul class="footer-col-links">
                <li><a href="/contacto">Preguntas frecuentes</a></li>
                <li><a href="/carrito/">Carrito</a></li>
                <li><a href="/terminos-y-condiciones">Términos y condiciones</a></li>
                <li><a href="/contacto">Contacto</a></li>
              </ul>
            </div>
          </div>
        </div>
  
        <!-- Bottom bar -->
        <div class="footer-bottom d-flex justify-content-lg-between justify-content-center align-items-center">
          <div>
            <span>&copy; {{ currentYear }} Fix Glass. Todos los derechos reservados</span>
          </div>
          <div class="footer-dev-right">
            <span class="footer-dev-label">Desarrollado por</span>
            <a
              class="footer-dev-link"
              href="https://permaculture.ar"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                class="footer-dev-logo mt-2"
                :src="permacultureLogo"
                alt="Permaculture"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useScrollReveal } from '@/composables/useScrollReveal';
import { getCategories } from '@/services/categories';
import { useSiteContact } from '@/composables/useSiteContact';
import permacultureLogo from '@/assets/images/permaculture-logo.svg';

const currentYear = new Date().getFullYear();

const {
  contactEmail,
  contactPhoneDisplay,
  contactMailto,
  telHref,
  instagramUrl,
} = useSiteContact();

const footerRef = ref(null);
const categories = ref([]);
const { observe } = useScrollReveal();

// Solo categorías padre (sin parent) para el footer, máximo 6
const footerCategories = computed(() => {
  const parents = categories.value.filter(cat => !cat.parent);
  return parents.slice(0, 3);
});

onMounted(async () => {
  if (footerRef.value) {
    observe(footerRef.value);
  }
  try {
    const response = await getCategories();
    const data = response.results || response;
    categories.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Error cargando categorías en footer:', err);
  }
});
</script>

<style scoped>
footer {
  background-color: #000 !important;
  padding-left: max(0px, env(safe-area-inset-left, 0px));
  padding-right: max(0px, env(safe-area-inset-right, 0px));
  padding-bottom: max(0.75rem, env(safe-area-inset-bottom, 0px));
}
/* ── Feature strip ── */
.feature-strip {
  padding: 0;
}

.feature-strip-inner {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 0 auto;
  gap: 10px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  color: var(--footer-text, #ccc);
  background-color: rgba(255, 255, 255, 0.08);
  font-size: 1rem;
  text-align: center;
  transition: background-color 0.2s;
  border-radius: 5px;
  font-weight: 500;
}

.feature-item i{
  color: var(--primary-light);
}

.feature-item:last-child {
  border-right: none;
}


.feature-icon {
  font-size: 1.5rem;
  color: var(--footer-text, #ccc);
  opacity: 0.8;
}

/* ── Main footer body ── */
footer {
  backdrop-filter: none;
}

.footer {
  color: var(--footer-text, #ccc);
  margin-top: 0;
  border-top: none;
}

.footer-body {
  margin: 0 auto;
}

.footer-main {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4rem;
  padding: clamp(2.25rem, 5vw, 3.5rem) 0 clamp(2rem, 4vw, 3rem);
  align-items: start;
}

.contact-item a {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* ── Newsletter ── */
.newsletter-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--footer-heading, #fff);
  letter-spacing: 0.01em;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.newsletter-form {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255,255,255,0.35);
  padding-bottom: 0.5rem;
  gap: 0;
  max-width: 450px;
}

.newsletter-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--footer-text, #ccc);
  font-size: 0.95rem;
  padding: 0.25rem 0;
}

.newsletter-input::placeholder {
  color: rgba(255,255,255,0.4);
}

.newsletter-btn {
  background: transparent;
  border: none;
  outline: none;
  color: var(--footer-heading, #fff);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0 0.25rem 1rem;
  letter-spacing: 0.03em;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.newsletter-btn:hover {
  opacity: 0.7;
}

.newsletter-desc {
  margin-top: 1.2rem;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
  max-width: 450px;
  line-height: 1.6;
}

/* ── Link columns ── */
.footer-links-grid {
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 0 3rem;
}

.footer-col-title {
  color: var(--footer-heading, #fff);
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
  letter-spacing: 0.04em;
}

.contact-title,
.footer-col-title {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  position: relative;
  text-shadow: 0 0 12px rgba(220, 38, 38, 0.2);
}

.contact-title::after,
.footer-col-title::after {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(220, 38, 38, 0.85);
  box-shadow: 0 0 10px rgba(220, 38, 38, 0.45);
}

.footer-contact i {
  color: rgba(248, 113, 113, 0.9);
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(220, 38, 38, 0.12);
  box-shadow: 0 0 12px rgba(220, 38, 38, 0.25);
}

.footer-dev-label {
  color: rgba(248, 113, 113, 0.75);
}

.footer-col-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-col-links li {
  margin-bottom: 0.65rem;
}

.footer-col-links a {
  color: rgba(255,255,255,0.78);
  text-decoration: none;
  font-size: 0.85rem;
  transition: color 0.2s;
  overflow-wrap: anywhere;
}

.footer-col-links a:hover {
  color: rgba(248, 113, 113, 0.9);
  text-shadow: 0 0 10px rgba(220, 38, 38, 0.3);
}

/* ── Bottom bar ── */
.footer-bottom {
  padding: 1.2rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.72);
  gap: 1rem;
  flex-wrap: wrap;
  text-align: left;
}

.footer-bottom > div {
  min-width: 0;
}

.dev {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.72);
}

.dev-link {
  color: rgba(255,255,255,0.65);
  text-decoration: none;
  transition: color 0.2s;
}

.dev-link:hover {
  color: #fff;
}

.footer-dev-right {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.footer-dev-label {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.74);
  letter-spacing: 0.04em;
}

.footer-dev-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.footer-dev-logo {
  height: 22px;
  width: auto;
  display: block;
  opacity: 0.9;
  filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.35));
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .footer-main {
    grid-template-columns: 1fr;
    gap: 2.5rem;
    text-align: center;
  }

  .contact-info {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .contact-item {
    justify-content: center;
  }

  .footer-links-grid {
    justify-items: center;
  }

  .footer-col-links {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .footer-bottom {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .footer-bottom .dev {
    justify-content: center;
  }

  .feature-strip-inner {
    grid-template-columns: repeat(2, 1fr);
  }

  .feature-item {
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.04);
  }

  .feature-item:nth-child(2),
  .feature-item:nth-child(4) {
    border-right: none;
  }
}

@media (max-width: 600px) {
  .feature-strip-inner {
    grid-template-columns: 1fr 1fr;
  }

  .footer-links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 2rem;
  }

  .newsletter-title {
    font-size: 1.25rem;
  }
}

@media (max-width: 479.98px) {
  .footer-links-grid {
    grid-template-columns: 1fr;
    gap: 1.75rem;
  }

  .contact-title {
    font-size: 1rem;
    letter-spacing: 0.02em;
  }
}
</style>
